import * as THREE from "three";
import type { Dimension, MatrixValues } from "@src/math/types";
import {
  captureCameraSnapshots,
  restoreCameraSnapshots,
} from "./camera-snapshot-adapter";
import {
  configureCameras,
  createOrthographicControls,
  createPerspectiveControls,
  resetCameraView,
} from "./camera-controls";
import { createGridPlane } from "./grid-plane";
import { writeTransformationMatrix } from "./linear-transform";
import { ArrowVisuals } from "./arrow-visuals";
import type { CameraSnapshots } from "../camera-snapshots";
import type { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import type { RenderState } from "../render-state";

const SCRATCH_TRANSFORMATION_MATRIX = new THREE.Matrix4();

export class TransformationScene {
  private readonly webglRenderer: THREE.WebGLRenderer;
  private readonly threeScene = new THREE.Scene();
  private readonly perspectiveCamera = new THREE.PerspectiveCamera(
    45,
    1,
    0.1,
    100,
  );
  private readonly orthographicCamera = new THREE.OrthographicCamera(
    -7,
    7,
    7,
    -7,
    0.1,
    100,
  );
  private readonly perspectiveControls: OrbitControls;
  private readonly orthographicControls: OrbitControls;
  private readonly gridRoot = new THREE.Group();
  private readonly gridPlanes = {
    xy: createGridPlane("xy"),
    xz: createGridPlane("xz"),
    yz: createGridPlane("yz"),
  };
  private readonly contentRoot = new THREE.Group();
  private readonly arrowVisuals = new ArrowVisuals(this.contentRoot);
  private dimension: Dimension = 3;
  private activeControlsDimension: Dimension = 3;
  private viewportWidth = 0;
  private viewportHeight = 0;
  private viewportPixelRatio = 0;
  private disposed = false;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly requestRender: () => void = () => {},
  ) {
    this.webglRenderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: true,
    });
    this.webglRenderer.setClearColor(0x101416, 1);

    this.contentRoot.add(this.gridRoot);
    this.gridRoot.add(
      this.gridPlanes.xy,
      this.gridPlanes.xz,
      this.gridPlanes.yz,
    );
    this.threeScene.add(this.contentRoot);
    this.threeScene.add(new THREE.AmbientLight(0xffffff, 0.55));
    const arrowLight = new THREE.DirectionalLight(0xffffff, 1.15);
    arrowLight.position.set(4, 7, 6);
    this.threeScene.add(arrowLight);

    configureCameras(this.perspectiveCamera, this.orthographicCamera);
    this.perspectiveControls = createPerspectiveControls(
      this.perspectiveCamera,
      this.canvas,
    );
    this.orthographicControls = createOrthographicControls(
      this.orthographicCamera,
      this.canvas,
    );
    const handleControlsChange = () => {
      this.requestRender();
    };
    this.perspectiveControls.addEventListener("change", handleControlsChange);
    this.orthographicControls.addEventListener("change", handleControlsChange);

    this.resize();
  }

  resetView(dimension = this.dimension): void {
    resetCameraView(
      dimension,
      this.perspectiveCamera,
      this.perspectiveControls,
      this.orthographicCamera,
      this.orthographicControls,
    );
  }

  getCameraSnapshots(): CameraSnapshots {
    return captureCameraSnapshots(
      this.orthographicCamera,
      this.orthographicControls,
      this.perspectiveCamera,
      this.perspectiveControls,
    );
  }

  restoreCameraSnapshots(snapshots: CameraSnapshots): void {
    restoreCameraSnapshots(
      snapshots,
      this.orthographicCamera,
      this.orthographicControls,
      this.perspectiveCamera,
      this.perspectiveControls,
    );
  }

  render<D extends Dimension>(state: RenderState<D>): boolean {
    if (this.disposed) return false;
    const resized = this.resize();
    const view = this.getDimensionView(state.dimension);
    this.dimension = state.dimension;
    this.updateGrid(view, state.transform, state.showGrid);
    this.arrowVisuals.updateBasis(
      state.dimension,
      state.transform,
      state.showBasis,
    );
    this.arrowVisuals.updateVectors(
      state.dimension,
      state.transform,
      state.vectors,
    );
    const controlsNeedAnotherFrame = this.updateActiveControls(view);
    this.webglRenderer.render(this.threeScene, view.camera);
    return resized || controlsNeedAnotherFrame;
  }

  resize(): boolean {
    const width = Math.max(1, this.canvas.clientWidth);
    const height = Math.max(1, this.canvas.clientHeight);
    const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
    const sizeChanged =
      width !== this.viewportWidth || height !== this.viewportHeight;
    const pixelRatioChanged = pixelRatio !== this.viewportPixelRatio;
    if (!sizeChanged && !pixelRatioChanged) return false;

    if (pixelRatioChanged) {
      this.viewportPixelRatio = pixelRatio;
      this.webglRenderer.setPixelRatio(pixelRatio);
    }
    if (!sizeChanged) return true;

    this.viewportWidth = width;
    this.viewportHeight = height;

    this.webglRenderer.setSize(width, height, false);

    this.perspectiveCamera.aspect = width / height;
    this.perspectiveCamera.updateProjectionMatrix();

    const aspect = width / height;
    const span = 7;
    this.orthographicCamera.left = -span * aspect;
    this.orthographicCamera.right = span * aspect;
    this.orthographicCamera.top = span;
    this.orthographicCamera.bottom = -span;
    this.orthographicCamera.updateProjectionMatrix();
    return true;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.perspectiveControls.dispose();
    this.orthographicControls.dispose();

    this.gridRoot.traverse((object) => {
      if (!(object instanceof THREE.LineSegments)) return;
      const line = object as THREE.LineSegments<
        THREE.BufferGeometry,
        THREE.Material
      >;
      line.geometry.dispose();
      line.material.dispose();
    });
    this.arrowVisuals.dispose();
    this.webglRenderer.dispose();
  }

  private getDimensionView(dimension: Dimension): DimensionView {
    switch (dimension) {
      case 2:
        return {
          dimension,
          camera: this.orthographicCamera,
          controls: this.orthographicControls,
          showSecondaryGridPlanes: false,
        };
      case 3:
        return {
          dimension,
          camera: this.perspectiveCamera,
          controls: this.perspectiveControls,
          showSecondaryGridPlanes: true,
        };
    }
  }

  private updateActiveControls(view: DimensionView): boolean {
    if (view.dimension !== this.activeControlsDimension) {
      this.perspectiveControls.enabled = view.dimension === 3;
      this.orthographicControls.enabled = view.dimension === 2;
      this.activeControlsDimension = view.dimension;
    }
    return view.controls.update();
  }

  private updateGrid(
    view: DimensionView,
    matrix: Readonly<MatrixValues>,
    visible: boolean,
  ): void {
    this.gridPlanes.xy.visible = visible;
    this.gridPlanes.xz.visible = visible && view.showSecondaryGridPlanes;
    this.gridPlanes.yz.visible = visible && view.showSecondaryGridPlanes;

    this.gridRoot.matrixAutoUpdate = false;
    this.gridRoot.matrix.copy(
      writeTransformationMatrix(
        SCRATCH_TRANSFORMATION_MATRIX,
        view.dimension,
        matrix,
      ),
    );
  }
}

interface DimensionView {
  readonly dimension: Dimension;
  readonly camera: THREE.Camera;
  readonly controls: OrbitControls;
  readonly showSecondaryGridPlanes: boolean;
}
